// Pantone color conversion utility using the pantone-colors library
import pantoneColorsLib from 'pantone-colors';

// The library provides a simple object mapping PMS codes to hex values
const pantoneHexMap = pantoneColorsLib.default || pantoneColorsLib;

// Convert hex to RGB helper
const hexToRgb = (hex) => {
  if (!hex || !hex.startsWith('#')) return null;
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? [
    parseInt(result[1], 16),
    parseInt(result[2], 16),
    parseInt(result[3], 16)
  ] : null;
};

/**
 * Convert Pantone/PMS code to RGB values
 * @param {string} pmsCode - Pantone code (e.g., "185", "PMS 185", "185C")
 * @returns {[number, number, number]|null} RGB array or null if not found
 */
export const pmsToRgb = (pmsCode) => {
  if (!pmsCode || typeof pmsCode !== 'string') return null;
  
  // Clean the input - handle various formats
  let cleanCode = pmsCode
    .trim()
    .toUpperCase()
    .replace(/^PMS\s*/i, '')   // Remove PMS prefix
    .replace(/^PANTONE\s*/i, '') // Remove PANTONE prefix
    .replace(/\s+/g, '');       // Remove all spaces
  
  // Try different variations of the code
  const variations = [
    cleanCode,                        // Original cleaned code
    cleanCode.replace(/[CU]$/, ''),   // Remove C (Coated) or U (Uncoated) suffix
    cleanCode.replace(/[CPU]$/, ''),  // Remove C, P, or U suffix
    cleanCode.toLowerCase(),          // Try lowercase
  ];
  
  // Also handle special color names
  const specialColors = {
    'BLACK': '0',
    'WHITE': '1',
    'REFLEXBLUE': '2728',
    'PROCESSBLUE': '3005',
    'PROCESSCYAN': 'P-3',
    'PROCESSMAGENTA': 'P-59',
    'PROCESSYELLOW': 'P-4',
    'PROCESSBLACK': 'P-179'
  };
  
  if (specialColors[cleanCode]) {
    variations.push(specialColors[cleanCode]);
  }
  
  // Try each variation
  for (const code of variations) {
    const hex = pantoneHexMap[code];
    if (hex) {
      return hexToRgb(hex);
    }
  }
  
  // If not found, return null
  console.warn(`Pantone color not found: ${pmsCode} (tried: ${variations.join(', ')})`);
  return null;
};

/**
 * Get all available Pantone colors
 * @returns {Array} Array of Pantone color objects
 */
export const getAllPantoneColors = () => {
  return Object.entries(pantoneHexMap).map(([code, hex]) => ({
    code,
    hex,
    rgb: hexToRgb(hex),
    name: `Pantone ${code}`
  }));
};

/**
 * Search Pantone colors by code
 * @param {string} query - Search query
 * @returns {Array} Matching Pantone colors
 */
export const searchPantoneColors = (query) => {
  const searchTerm = query.toLowerCase().replace(/[^0-9a-z]/g, '');
  return getAllPantoneColors().filter(color => 
    color.code.toLowerCase().includes(searchTerm)
  );
};

/**
 * Get the count of available Pantone colors
 * @returns {number} Number of Pantone colors in the library
 */
export const getPantoneColorCount = () => {
  return Object.keys(pantoneHexMap).length;
};

export default { pmsToRgb, getAllPantoneColors, searchPantoneColors, getPantoneColorCount };